/** All valid device-ui feature IDs. */
const VALID_DEVICE_UI_FEATURE_IDS = [
	// Settings — page-level
	'settings.general',
	'settings.firmware',
	'settings.operation-mode',
	'settings.output-mode',
	'settings.mute-control',
	'settings.call-status',
	'settings.wireless-mics',
	'settings.licensing',
	'settings.auracast-licensing',
	'settings.ip-configuration',
	'settings.ntp-date-time',
	'settings.8021x',
	'settings.permissions',
	'settings.audio-encryption',
	'settings.services',
	'settings.bluetooth',
	'settings.lights',
	'settings.logic-control',
	'settings.factory-reset',
	'settings.pc-info',
	// Settings — sub-feature-level
	'settings.general.event-log-export',
	'settings.wireless-mics.rf-spectrum-scanner',
	'settings.services.cloud-claim-status',
	'settings.factory-reset.debug-mode',
	'settings.factory-reset.password-confirmation',
	// Metering
	'metering'
] as const;

/**
 * Union type of all valid device-ui feature IDs.
 *
 * Feature IDs use a dot-separated hierarchical naming convention.
 * Disabling a parent implicitly disables all its children at runtime
 * (e.g. disabling `'settings.wireless-mics'` also disables `'settings.wireless-mics.rf-spectrum-scanner'`).
 *
 * @example
 * ```typescript
 * import { DeviceUiFeatureId, provideDeviceUiFeatureToggle } from '@shure/device-ui/shared/feature-toggle';
 *
 * // In host app config:
 * provideDeviceUiFeatureToggle(['settings.ip-configuration', 'metering'])
 * ```
 */
export type DeviceUiFeatureId = (typeof VALID_DEVICE_UI_FEATURE_IDS)[number];

export const DEVICE_UI_FEATURE_IDS: readonly DeviceUiFeatureId[] = VALID_DEVICE_UI_FEATURE_IDS;

/**
 * Maps settings route paths to their corresponding {@link DeviceUiFeatureId}.
 *
 * Some routes use a path that differs from the feature ID suffix
 * (e.g. `network` → `settings.ip-configuration`, `reset` → `settings.factory-reset`).
 */
export const SETTINGS_ROUTE_PATH_TO_FEATURE_ID: ReadonlyMap<string, DeviceUiFeatureId> = new Map<
	string,
	DeviceUiFeatureId
>([
	['general', 'settings.general'],
	['firmware', 'settings.firmware'],
	['operation-mode', 'settings.operation-mode'],
	['output-mode', 'settings.output-mode'],
	['mute-control', 'settings.mute-control'],
	['call-status', 'settings.call-status'],
	['wireless-mics', 'settings.wireless-mics'],
	['licensing', 'settings.licensing'],
	['auracast-licensing', 'settings.auracast-licensing'],
	['network', 'settings.ip-configuration'],
	['8021x', 'settings.8021x'],
	['ntp-date-time', 'settings.ntp-date-time'],
	['permissions', 'settings.permissions'],
	['audio-encryption', 'settings.audio-encryption'],
	['services', 'settings.services'],
	['bluetooth', 'settings.bluetooth'],
	['lights', 'settings.lights'],
	['logic-control', 'settings.logic-control'],
	['reset', 'settings.factory-reset'],
	['pc-info', 'settings.pc-info']
]);
